import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Table from '@/components/common/Table';
import Button from '@/components/common/Button';
import { ReactComponent as RiEditLine } from '@/assets/icons/ri-edit-line.svg';
import { RootState } from '@/store';
import {constructTableMetaFromPaging2} from '@/utils/paging';
import { convertToLocalTime } from '@/utils';

const colors = {
	CREATED: 'bg-purple-subtle text-purple-secondary',
	ACTIVE: 'bg-green-subtle text-green-success',
	INACTIVE: 'bg-red-subtle text-red-secondary',
	EXPIRED: 'bg-grey-subtle text-grey-dark',
	CONFIRMED: 'bg-blue-light text-blue-light bg-opacity-10',
	PAID: 'text-green-secondary bg-green-secondary bg-opacity-10'
};

const renderAction = (navigate, v) => (
	<Button
		onClick={() => {navigate(`/dashboard/subscriptions/edit/${v}`)}}
		type={null}
		variant="secondary"
		className="border-stroke-subtle border px-4"
	>
		<div className="flex gap-2 justify-center items-center h-4">
			<RiEditLine />
			Edit
		</div>
	</Button>
);
const renderStatus = (status) => (
	<div
		className={`flex px-4 py-1 w-fit rounded capitalize font-medium
    ${colors[status]}`}
	>
		{status}
	</div>
);
const renderCreatedDate = (date) => {
	const newDate = new Date(date);
	return (
		<div>
			{newDate.toLocaleDateString()},{' '}
			{newDate.toLocaleTimeString('en-GB', {
				hour: '2-digit',
				minute: '2-digit',
			})}
		</div>
	);
};
const renderDate = (date) => {
	const newDate = new Date(convertToLocalTime(date));
	return (
		<div>
			{newDate.toLocaleString('id-ID', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
			})}
		</div>
	);
};

const renderCustomer = (customer) => (
	<div className="flex flex-col gap-1">
		<div className="text-jsm font-bold text-black-primary">{customer.name}</div>
	</div>
);

const HEADERS: TableHeader[] = [
	{
		label: 'SUBSCRIPTION ID',
		key: 'subscriptionId',
	},
	{
		label: 'Customer',
		key: 'customer',
	},
	{
		label: 'Start Date',
		key: 'startDate',
	},
	{
		label: 'Renew Date',
		key: 'expiresAt',
	},
	{
		label: 'Status',
		key: 'status',
	},
	{
		label: 'Created Date',
		key: 'createdDate',
	},
	{
		label: 'Action',
		key: 'actionId',
	},
];
const constructSubscriptionRowData = (
	subs: Subscription
): TempSubscriptionVO => ({
	subscriptionId: subs.custom_id,
	customer: {
		name: subs?.customer?.name ?? '',
		club: subs?.customer?.club_name ?? '',
	},
	startDate: subs.start_date,
	expiresAt: subs.expires_at,
	createdDate: subs.created_at,
	status: subs.status,
	actionId: subs.id,
});

const constructTableBody = (subs: Subscription[], navigate: Function): TableBody => ({
	data: subs.map((s) => ({
		id: s.id,
		value: constructSubscriptionRowData(s),
	})),
	component: {
		subscriptionId: (v) => (
			<div className="text-jsm text-black-primary font-bold">{v}</div>
		),
		customer: (v) => renderCustomer(v),
		actionId: (v) => renderAction(navigate, v),
		status: (v) => renderStatus(v),
		startDate: (v) => renderDate(v),
		expiresAt: (v) => renderDate(v),
		createdDate: (v) => renderCreatedDate(v),
	},
});
const SORT: TableSort = {
	type: 'SERVER',
	handler: () => {},
};


const SubscriptionTable = () => {
	const navigate = useNavigate()
	const { subscriptions, paging } = useSelector(
		(state: RootState) => state.subscription.state
	);

	return (
		<Table
			headers={HEADERS}
			body={constructTableBody(subscriptions, navigate)}
			sort={SORT}
			meta={constructTableMetaFromPaging2(paging)}
		/>
	);
};

export default SubscriptionTable;
